import { create } from 'zustand'
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth'
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '../config/firebase'
import { Admin } from '../models/User'

interface AuthState {
  user: FirebaseUser | null
  admin: Admin | null
  loading: boolean
  error: string | null
  initialized: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  initialize: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  admin: null,
  loading: true,
  error: null,
  initialized: false,

  signIn: async (email: string, password: string) => {
    set({ loading: true, error: null })
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password)

      // Check if user is an admin
      const adminDoc = await getDoc(doc(db, 'admins', credential.user.uid))
      if (!adminDoc.exists()) {
        await firebaseSignOut(auth)
        throw new Error('您沒有管理員權限')
      }

      const adminData = adminDoc.data()
      if (!adminData.isActive) {
        await firebaseSignOut(auth)
        throw new Error('您的帳號已被停用')
      }

      await updateDoc(doc(db, 'admins', credential.user.uid), {
        lastSeen: serverTimestamp(),
      })

      set({
        user: credential.user,
        admin: {
          id: adminDoc.id,
          email: adminData.email,
          displayName: adminData.displayName,
          role: adminData.role,
          createdAt: adminData.createdAt?.toDate(),
          isActive: adminData.isActive,
        },
        loading: false,
      })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '登入失敗',
      })
      throw error
    }
  },

  signOut: async () => {
    set({ loading: true })
    try {
      await firebaseSignOut(auth)
      set({ user: null, admin: null, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '登出失敗',
      })
    }
  },

  initialize: () => {
    if (get().initialized) return

    onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Fetch admin data
        const adminDoc = await getDoc(doc(db, 'admins', user.uid))
        if (adminDoc.exists()) {
          const adminData = adminDoc.data()
          // 更新最後上線時間（含 token 自動 refresh 後的頁面訪問）
          updateDoc(doc(db, 'admins', user.uid), { lastSeen: serverTimestamp() })
          set({
            user,
            admin: {
              id: adminDoc.id,
              email: adminData.email,
              displayName: adminData.displayName,
              role: adminData.role,
              createdAt: adminData.createdAt?.toDate(),
              isActive: adminData.isActive,
            },
            loading: false,
            initialized: true,
          })
        } else {
          set({ user: null, admin: null, loading: false, initialized: true })
        }
      } else {
        set({ user: null, admin: null, loading: false, initialized: true })
      }
    })
  },
}))

// Initialize auth listener
useAuthStore.getState().initialize()
